// imports
import clsx from 'clsx';
import remarkGfm from 'remark-gfm';
import Markdown from 'react-markdown';
import Modal from 'bootstrap/js/dist/modal';
import React, {
  ReactElement, ReactNode,
  useRef, forwardRef, useImperativeHandle, useEffect
} from 'react';
import { IMAGE } from '../../assets';
import Checkbox from '../../atoms/checkbox';
import PaperBG from '../../atoms/background/paper';

// interfaces / types / consts
interface Props {
  title: string;
  message: string;
  body?: JSX.Element;
  className?: string;
  neverAsk?: boolean;
  footer?: JSX.Element;
  hideCloseButton?: boolean;
  onClosed?: () => void;
  onCheckboxChanged?: () => void;
}

// private components
function LinkRenderer(props: unknown): ReactElement {
  const { href, children } = props as { href: string; children: ReactNode };
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

// react component
const Prompt = forwardRef((props: Props, ref) => {
  const {
    title, message, body, footer, className,
    neverAsk, onClosed, onCheckboxChanged, hideCloseButton
  } = props;
  // hooks configuration
  const modalRef = useRef<HTMLDivElement>(null);

  // private methods
  const getModal = (create = false): Modal | null => {
    const modalElement = modalRef.current;
    if (modalElement) {
      let bsModal = Modal.getInstance(modalElement);
      if (!bsModal && create) { bsModal = new Modal(modalElement, { keyboard: false, backdrop: 'static' }); }
      return bsModal;
    }
    return null;
  };
  const show = (): void => {
    const bsModal = getModal(true);
    const elems = document.getElementsByClassName('modal-backdrop fade show');
    if (className) elems[elems.length - 1]?.classList.add(className);
    bsModal?.show();
  };
  const hide = (): void => {
    if (onClosed) { onClosed(); }
    const bsModal = getModal();
    bsModal?.hide();
  };
  useImperativeHandle(ref, () => ({ show, hide }));

  useEffect(() => () => {
    const elems = document.getElementsByClassName('modal-backdrop fade show');
    const bsModal = getModal();
    if (!bsModal) {
      // const elem = document.getElementsByClassName('modal-backdrop fade show')[0];
      for (let i = 0; i < elems.length; i += 1) {
        elems[i]?.remove();
      }
    }
  }, []);

  // render
  return (
    <div className={clsx('prompt modal fade', className)} ref={modalRef} tabIndex={-1} aria-labelledby="promptModal" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4">
          <div className="modal-body">
            <PaperBG fold flip />
            <div className="body-content">
              {!hideCloseButton && <button type="button" onClick={hide} aria-label="Close" className="btn-close" />}
              <div className="header">
                <img src={IMAGE.HERO_TITLE_MOTIF} alt="Left Motif" />
                <h2 className="title">{title}</h2>
                <img className="right-motif" src={IMAGE.HERO_TITLE_MOTIF} alt="Right Motif" />
              </div>
              <div className="content">
                <Markdown className="msg" remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>{message}</Markdown>
                <div className="body">{body}</div>
                {onCheckboxChanged && (
                  <Checkbox
                    checked={neverAsk || false}
                    label="Don't ask me again."
                    onChanged={onCheckboxChanged}
                  />
                )}
              </div>
              <div className="footer">{footer}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// display name
Prompt.displayName = 'Prompt';

// exports
export default Prompt;

// default props
Prompt.defaultProps = {
  className: '',
  neverAsk: false,
  body: undefined,
  footer: undefined,
  hideCloseButton: false,
  onCheckboxChanged: undefined,
  onClosed: () => null
};
